import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  useTheme,
  Skeleton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Block as BlockIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { Agent } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';
import { computeConversationCount } from '../../utils/chatHistory';
import WhatsAppStatusIcon from '../WhatsAppStatusIcon';
import ConnectionsSection from './ConnectionsSection';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import toast from 'react-hot-toast';
import AgentUsersDialog from './AgentUsersDialog';
import AgentChatTestDialog from './AgentChatTestDialog';
import AgentCreditUsageComponent from './AgentCreditUsage';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

interface AgentDetailsProps {
  onEdit: (agentId: string) => void;
  onDelete: (agentId: string) => void;
  onDuplicate: (agentId: string) => void;
  onBack: () => void;
  refreshKey?: number;
}

const AgentDetails: React.FC<AgentDetailsProps> = ({
  onEdit,
  onDelete,
  onDuplicate,
  onBack,
  refreshKey
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [agentDetails, setAgentDetails] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [operationLoading, setOperationLoading] = useState<string | null>(null);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const connection = useConnectionStatus({ agentId: agentId || '', agent, onAgentUpdate: (a) => setAgent(a) });
  const getConversationCount = (details: any): number => computeConversationCount(details);

  useEffect(() => {
    if (agentId) {
      loadAgentDetails(agentId);
    }
  }, [agentId, refreshKey]);

  const loadAgentDetails = async (id: string) => {
    try {
      setLoading(true);
      const [agentData, detailsData] = await Promise.all([
        agentApi.getAgent(id),
        Promise.resolve(null)
        // agentApi.getAgentDetails(id).catch(() => null) // Details might not be available
      ]);

      setAgent(agentData);
      // setAgentDetails(detailsData);
    } catch (error) {
      console.error('Error loading agent details:', error);
      toast.error(t('agents.errors.loadDetailsError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!agent) return;

    try {
      setOperationLoading('delete');
      await agentApi.deleteAgent(agent.id);
      onDelete(agent.id);
      setDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error(t('agents.errors.deleteError'));
    } finally {
      setOperationLoading(null);
    }
  };

  const handleDuplicate = async () => {
    if (!agent) return;

    try {
      setOperationLoading('duplicate');
      await onDuplicate(agent.id);
    } catch (error) {
      console.error('Error duplicating agent:', error);
      toast.error(t('agents.errors.duplicateError'));
    } finally {
      setOperationLoading(null);
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'enabled':
        return 'success';
      case 'disabled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'enabled':
        return t('agents.active');
      case 'disabled':
        return t('agents.inactive');
      default:
        return t('agents.unknown');
    }
  };

  if (loading) {
    return (
      <Box>
        {/* Header Skeleton */}
        <Box sx={{ mb: 3 }}>
          {/* Mobile Layout Skeleton */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1 }} />
              <Skeleton variant="text" width={200} height={40} sx={{ mr: 0.5 }} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="circular" width={32} height={32} />
              ))}
            </Box>
          </Box>

          {/* Desktop Layout Skeleton */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
              <Skeleton variant="text" width={250} height={40} sx={{ mr: 1 }} />
              <Skeleton variant="circular" width={40} height={40} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="circular" width={40} height={40} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Content Skeleton */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: '2fr 1fr'
            },
            gap: { xs: 2, md: 3 }
          }}
        >
          {/* Left Column Skeleton */}
          <Box>
            {/* Informações Gerais Skeleton */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
              <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Skeleton variant="rounded" width={80} height={32} />
                <Skeleton variant="rounded" width={100} height={32} />
                <Skeleton variant="rounded" width={90} height={32} />
              </Box>
            </Paper>

            {/* Conexões Skeleton */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
              <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Skeleton variant="rounded" width="100%" height={60} />
                <Skeleton variant="rounded" width="100%" height={60} />
              </Box>
            </Paper>
          </Box>

          {/* Right Column Skeleton */}
          <Box>
            {/* Estatísticas Skeleton */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
              <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
              <List dense>
                {[1, 2, 3, 4].map((i) => (
                  <ListItem key={i} sx={{ px: { xs: 1, md: 2 } }}>
                    <ListItemText
                      primary={<Skeleton variant="text" width="60%" />}
                      secondary={<Skeleton variant="text" width="40%" />}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Uso de Créditos Skeleton */}
            <Paper elevation={2} sx={{ mb: 3, p: { xs: 1, md: 2 } }}>
              <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={120} />
            </Paper>

            {/* Configurações Skeleton */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
              <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
              <List dense>
                {[1, 2, 3].map((i) => (
                  <ListItem key={i} sx={{ px: { xs: 1, md: 2 } }}>
                    <ListItemText
                      primary={<Skeleton variant="text" width="70%" />}
                      secondary={<Skeleton variant="text" width="30%" />}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!agent) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="error">
          {t('agents.notFound')}
        </Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {/* Mobile Layout */}
        <Box sx={{ 
          display: { xs: 'block', md: 'none' }
        }}>
          {/* Title Row with Status Icon - Aligned with Back Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <IconButton onClick={onBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography 
              variant="h5" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mr: 0.5
              }}
            >
              {agent.name}
            </Typography>
            <WhatsAppStatusIcon agentId={agent.id} agent={agent} size="medium" connection={connection} />
            {agent.shouldBlockAI && (connection.status.zapi.isConnected || connection.status.whatsappNative.isConnected) && (
              <Tooltip title={t('agents.aiBlocked')}>
                <BlockIcon
                  sx={{
                    color: 'error.main',
                    fontSize: '1.5rem',
                    ml: 0.5
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Action Icons Row - Centered */}
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5, 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Tooltip title={t('agents.testChat')}>
              <span>
                <IconButton 
                  color="primary" 
                  onClick={() => setChatDialogOpen(true)} 
                  aria-label={t('agents.testChat')}
                  size="small"
                >
                  <ChatIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Homologação">
              <span>
                <IconButton 
                  color="secondary" 
                  onClick={() => {
                    if (currentOrganization && agentId) {
                      navigate(`/homologation/${currentOrganization.id}/${agentId}`);
                    }
                  }} 
                  aria-label="Homologação"
                  size="small"
                >
                  <VerifiedUserIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('agents.agentUsers')}>
              <span>
                <IconButton 
                  onClick={() => setUsersDialogOpen(true)} 
                  aria-label={t('agents.agentUsers')}
                  size="small"
                >
                  <PeopleIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton 
                  onClick={() => onEdit(agent.id)} 
                  aria-label={t('common.edit')}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('agents.duplicate')}>
              <span>
                <IconButton 
                  onClick={handleDuplicate} 
                  aria-label={t('agents.duplicate')} 
                  disabled={operationLoading === 'duplicate'}
                  size="small"
                >
                  {operationLoading === 'duplicate' ? <CircularProgress size={16} /> : <DuplicateIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton 
                  color="error" 
                  onClick={() => setDeleteDialog(true)} 
                  aria-label={t('common.delete')}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Desktop Layout */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' }, 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                {agent.name}
              </Typography>
            </Box>
            <WhatsAppStatusIcon agentId={agent.id} agent={agent} size="large" connection={connection} />
            {agent.shouldBlockAI && (connection.status.zapi.isConnected || connection.status.whatsappNative.isConnected) && (
              <Tooltip title={t('agents.aiBlocked')}>
                <BlockIcon
                  sx={{
                    color: 'error.main',
                    fontSize: '1.8rem'
                  }}
                />
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('agents.testChat')}>
              <span>
                <IconButton color="primary" onClick={() => setChatDialogOpen(true)} aria-label={t('agents.testChat')}>
                  <ChatIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Homologação">
              <span>
                <IconButton 
                  color="secondary" 
                  onClick={() => {
                    if (currentOrganization && agentId) {
                      navigate(`/homologation/${currentOrganization.id}/${agentId}`);
                    }
                  }} 
                  aria-label="Homologação"
                >
                  <VerifiedUserIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('agents.agentUsers')}>
              <span>
                <IconButton onClick={() => setUsersDialogOpen(true)} aria-label={t('agents.agentUsers')}>
                  <PeopleIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton onClick={() => onEdit(agent.id)} aria-label={t('common.edit')}>
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('agents.duplicate')}>
              <span>
                <IconButton onClick={handleDuplicate} aria-label={t('agents.duplicate')} disabled={operationLoading === 'duplicate'}>
                  {operationLoading === 'duplicate' ? <CircularProgress size={20} /> : <DuplicateIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton color="error" onClick={() => setDeleteDialog(true)} aria-label={t('common.delete')}>
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: '2fr 1fr'
          },
          gap: { xs: 2, md: 3 }
        }}
      >
        {/* Informações Gerais */}
        <Box>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('agents.generalInfo')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1,
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}>
                <Chip
                  label={getStatusText(agent.status)}
                  color={getStatusColor(agent.status)}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#000' : undefined,
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }}
                />
                <Chip
                  label={agent.model}
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
                <Chip
                  label={t('agents.delay', { delay: agent.responseDelay })}
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Conexões */}
          {agent.id && (
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
              <ConnectionsSection
                agentId={agent.id}
                agent={agent}
                onAgentUpdate={setAgent}
                connection={connection}
              />
            </Paper>
          )}
        </Box>

        {/* Estatísticas */}
        <Box>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('agents.statistics')}
            </Typography>

            <List dense>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.prompts')}
                  secondary={agent.prompts?.length || 0}
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.conversationHistory')}
                  secondary={getConversationCount(agentDetails) || 0}
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.lastActivity')}
                  secondary={
                    agent.lastUpdateTime
                      ? new Date(agent.lastUpdateTime).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : t('agents.never')
                  }
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.creationDate')}
                  secondary={
                    agent.creationTime
                      ? new Date(agent.creationTime).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : t('agents.notAvailable')
                  }
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
            </List>
          </Paper>

          {/* Uso de Créditos */}
          <Paper elevation={2} sx={{ mb: 3, p: { xs: 1, md: 0 } }}>
            <AgentCreditUsageComponent agentId={agent.id} />
          </Paper>

          {/* Configurações */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom>
              {t('agents.settings')}
            </Typography>

            <List dense>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.requireName')}
                  secondary={agent.exigirNome ? t('common.yes') : t('common.no')}
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.requireFullName')}
                  secondary={agent.exigirNomeCompleto ? t('common.yes') : t('common.no')}
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.doNotAccumulateMessages')}
                  secondary={agent.naoAcumularMensagens ? t('common.yes') : t('common.no')}
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                <ListItemText
                  primary={t('agents.blockAI')}
                  secondary={agent.shouldBlockAI ? t('common.yes') : t('common.no')}
                  primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              </ListItem>
              {agent.agentConfig?.followUp !== undefined && agent.agentConfig?.followUp > 0 && (
                <ListItem sx={{ px: { xs: 1, md: 2 } }}>
                  <ListItemText
                    primary={t('agents.followUp')}
                    secondary={t('agents.followUpMinutes', { minutes: agent.agentConfig.followUp })}
                    primaryTypographyProps={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                    secondaryTypographyProps={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>
      </Box>

      {/* Dialog de confirmação para deletar */}
      <AgentUsersDialog open={usersDialogOpen} onClose={() => setUsersDialogOpen(false)} agentId={agent.id} agentName={agent.name} />
      <AgentChatTestDialog open={chatDialogOpen} onClose={() => setChatDialogOpen(false)} agentId={agent.id} agentName={agent.name} />
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>{t('agents.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('agents.deleteConfirm.messageDetails', { name: agent.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={operationLoading === 'delete'}
          >
            {operationLoading === 'delete' ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentDetails;